"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var ksys_app_template_1 = require("@kurtosys/ksys-app-template");
var DatePickerUtils_1 = require("../../base/DatePicker/DatePickerUtils");
var ChartBaseFunctions_1 = require("./ChartBaseFunctions");
var DEFAULT_COUNT = 5;
var DEFAULT_INTERVALS = [
    0.01, 0.02, 0.05,
    0.1, 0.2, 0.5,
    1, 2, 5,
    10, 20, 50,
    100, 200, 500,
    1000, 2000, 5000,
    10000, 20000, 50000,
    100000, 200000, 500000,
    1000000, 2000000, 5000000,
    10000000, 20000000, 50000000,
    100000000, 200000000, 500000000
];
var DEFAULT_TOP_BAR_LABELS = false;
var DEFAULT_PRECISION = 0;
var DEFAULT_TICK_PADDING_THRESHOLD_PERCENTAGE = 35;
var DEFAULT_DATE_INTERVAL_OPTIONS = {
    biannually: {
        enabled: true,
        interval: 6,
    },
    quarterly: {
        enabled: true,
        interval: 3,
    },
    monthly: {
        enabled: true,
        interval: 1,
    },
    biweekly: {
        enabled: true,
        interval: 2,
    },
    weekly: {
        enabled: true,
        interval: 1,
    },
    daily: {
        enabled: true,
        interval: 1,
    },
};
var TickHandler = /** @class */ (function () {
    function TickHandler(options) {
        this.options = this.orchestrateOptions(options);
        this.defaultSyncTickOptions();
    }
    TickHandler.prototype.orchestrateOptions = function (options) {
        return ksys_app_template_1.utils.object.deepMergeObjects(this.defaultOptions, options);
    };
    TickHandler.prototype.defaultSyncTickOptions = function () {
        if (this.options.syncTickOptions) {
            var syncTickOptions = this.options.syncTickOptions;
            if (syncTickOptions.type === 'date') {
                syncTickOptions.dateIntervalOptions = ksys_app_template_1.utils.object.deepMergeObjects(DEFAULT_DATE_INTERVAL_OPTIONS, syncTickOptions.dateIntervalOptions);
            }
        }
    };
    Object.defineProperty(TickHandler.prototype, "defaultOptions", {
        get: function () {
            var response = {
                count: DEFAULT_COUNT,
                intervals: DEFAULT_INTERVALS,
                usingTopBarLabels: DEFAULT_TOP_BAR_LABELS,
                precision: DEFAULT_PRECISION,
                tickPaddingThresholdPercentage: DEFAULT_TICK_PADDING_THRESHOLD_PERCENTAGE,
            };
            return response;
        },
        enumerable: true,
        configurable: true
    });
    TickHandler.prototype.getTicksForSeries = function (data, seriesKeys, point, axis, center, persistQuadrants) {
        if (data && seriesKeys) {
            var getKeyFns = this.getFunctionsForSeriesKeys(seriesKeys);
            var domain = this.getDomain(data, getKeyFns);
            if (domain) {
                var min = domain.min, max = domain.max;
                var ticks = this.getTicksAroundPoint(ksys_app_template_1.utils.typeChecks.isUndefined(point) ? 1 : point, max, min, ksys_app_template_1.utils.typeChecks.isUndefined(center) ? false : center, ksys_app_template_1.utils.typeChecks.isUndefined(persistQuadrants) ? false : persistQuadrants);
                return ticks;
            }
        }
        return;
    };
    TickHandler.prototype.getTicksForLogScale = function (point, actualMax) {
        var current = point;
        var ticks = [current];
        while (current <= actualMax) {
            current = current * 2;
            ticks.push(current);
        }
        return ticks;
    };
    TickHandler.prototype.getTicksAroundPoint = function (point, max, min, center, persistQuadrants) {
        var _a = this.options, _b = _a.count, count = _b === void 0 ? DEFAULT_COUNT : _b, _c = _a.intervals, intervals = _c === void 0 ? DEFAULT_INTERVALS : _c;
        var actualMax = Math.max(max, point);
        var actualMin = Math.min(min, point);
        var gap = this.applyPrecision(actualMax - actualMin);
        var bestInterval = intervals.find(function (interval) { return interval >= gap / count; });
        var ticks = [];
        var elementProps = this.options.elementProps;
        ticks.push(point);
        if (elementProps && elementProps.scale === 'log' && point > 0) {
            ticks = this.getTicksForLogScale(point, actualMax);
        }
        else {
            if (bestInterval) {
                var maxRunner = point;
                while (maxRunner < actualMax) {
                    maxRunner += bestInterval;
                    this.pushTick(ticks, maxRunner);
                }
                var minRunner = point;
                while (minRunner > actualMin) {
                    minRunner -= bestInterval;
                    this.pushTick(ticks, minRunner);
                }
                if (center) {
                    var pointsAfterPivot = ticks.reduce(function (arr, number) {
                        if (number > point) {
                            return arr += 1;
                        }
                        return arr;
                    }, 0);
                    var pointsBeforePivot = ticks.reduce(function (arr, number) {
                        if (number < point) {
                            return arr += 1;
                        }
                        return arr;
                    }, 0);
                    if (pointsAfterPivot > pointsBeforePivot) {
                        for (var i = 0; i <= pointsAfterPivot; i++) {
                            if (i > pointsBeforePivot) {
                                minRunner -= bestInterval;
                                this.pushTick(ticks, minRunner);
                            }
                        }
                    }
                    else if (pointsAfterPivot < pointsBeforePivot) {
                        for (var i = 0; i <= pointsBeforePivot; i++) {
                            if (i > pointsAfterPivot) {
                                maxRunner += bestInterval;
                                this.pushTick(ticks, minRunner);
                            }
                        }
                    }
                }
                if (this.options.usingTopBarLabels && this.options.tickPaddingThresholdPercentage) {
                    var maxTick = Math.max.apply(Math, ticks);
                    if (maxTick !== point) {
                        var maxDiff = (maxTick - actualMax) / bestInterval * 100;
                        if (Math.abs(maxDiff) < this.options.tickPaddingThresholdPercentage) {
                            this.pushTick(ticks, maxTick + bestInterval);
                        }
                    }
                    var minTick = Math.min.apply(Math, ticks);
                    if (minTick !== point) {
                        var minDiff = (minTick - actualMin) / bestInterval * 100;
                        if (Math.abs(minDiff) < this.options.tickPaddingThresholdPercentage) {
                            this.pushTick(ticks, minTick - bestInterval);
                        }
                    }
                }
            }
            if (persistQuadrants && bestInterval) {
                for (var i = Math.min.apply(Math, ticks); i >= 0; i = i - bestInterval) {
                    this.pushTick(ticks, i - bestInterval);
                }
                for (var i = Math.max.apply(Math, ticks); i <= 0; i = i + bestInterval) {
                    this.pushTick(ticks, i + bestInterval);
                }
            }
        }
        ticks = ksys_app_template_1.utils.collection.unique(ticks, function (e) { return e.toString(); });
        var sortedTicks = ticks.sort(function (a, b) { return a - b; });
        return sortedTicks;
    };
    TickHandler.prototype.applyPrecision = function (number) {
        return parseFloat(number.toFixed(this.options.precision));
    };
    TickHandler.prototype.pushTick = function (ticks, tick) {
        var floatPrecisionFix = this.applyPrecision(tick); // handling floating point precision
        ticks.push(floatPrecisionFix);
    };
    TickHandler.prototype.getDomain = function (data, getKeyFns) {
        var min = null;
        var max = null;
        for (var _i = 0, data_1 = data; _i < data_1.length; _i++) {
            var row = data_1[_i];
            if (row) {
                for (var _a = 0, getKeyFns_1 = getKeyFns; _a < getKeyFns_1.length; _a++) {
                    var getKeyFn = getKeyFns_1[_a];
                    if (getKeyFn) {
                        var value = getKeyFn(row);
                        if (!Number.isNaN(value) && !ksys_app_template_1.utils.typeChecks.isNullOrUndefined(value)) {
                            min = min === null ? value : Math.min(min, value);
                            max = max === null ? value : Math.max(max, value);
                        }
                    }
                }
            }
        }
        if (max === null) {
            max = 0;
        }
        if (min === null) {
            min = 0;
        }
        max = this.dataMaxFn(max);
        min = this.dataMinFn(min);
        return { max: max, min: min };
    };
    TickHandler.prototype.getFunctionsForSeriesKeys = function (series) {
        var response = [];
        var _loop_1 = function (serie) {
            response.push(function (row) { return row[serie]; });
        };
        for (var _i = 0, series_1 = series; _i < series_1.length; _i++) {
            var serie = series_1[_i];
            _loop_1(serie);
        }
        return response;
    };
    TickHandler.prototype.dataMinFn = function (dataMin) {
        return Math.floor(dataMin * 100) / 100;
    };
    TickHandler.prototype.dataMaxFn = function (dataMax) {
        return Math.ceil(dataMax * 100) / 100;
    };
    /**
     * Will generate the ticks that are in a sequence based on the syncTickOptions
     *
     * @param data The axis data extracted from the dataset as an array
     * @param tickCount The maximum amounts of ticks that should be calculated
     * @returns Array of tick data
     */
    TickHandler.prototype.getSyncedTicksFromData = function (data, tickCount) {
        var syncTickOptions = this.options.syncTickOptions;
        if (data && syncTickOptions) {
            if (syncTickOptions.type === 'date' && syncTickOptions.dateIntervalOptions) {
                var _a = syncTickOptions.dateIntervalOptions, daily = _a.daily, biweekly = _a.biweekly, weekly = _a.weekly, monthly = _a.monthly, quarterly = _a.quarterly, biannually = _a.biannually;
                var dataStartDate = data[0];
                var dataEndDate = data[data.length - 1];
                // The system should find the best fit interval between ticks where they will fall on a sequential data point
                // and that generated amount matches the required tickCount exactly
                // Starting with days, work out how many ticks will fit in the data date range based on the configured interval
                // If the resulting tick count is greater than the tickCount then try the next configuration until the resulting
                // ticks is equal to or less than the tickCount, include the starting tick in the difference
                var dataDateRangeInDays = ksys_app_template_1.utils.date.getDateDifference(dataEndDate, dataStartDate, 'DAYS');
                var dataDateRangeInWeeks = ksys_app_template_1.utils.date.getDateDifference(dataEndDate, dataStartDate, 'WEEKS');
                var dataDateRangeInMonths = ksys_app_template_1.utils.date.getDateDifference(dataEndDate, dataStartDate, 'MONTHS');
                var dataDateRangeInYears = ksys_app_template_1.utils.date.getDateDifference(dataEndDate, dataStartDate, 'YEARS');
                // The intervals are defaulted
                if (daily && daily.enabled && daily.interval) {
                    // A tick per day defaulting to 1 day
                    var interval_1 = daily.interval;
                    var dailyTickCount = this.getPeriodTickCount(tickCount, dataDateRangeInDays, interval_1);
                    if (dailyTickCount) {
                        return this.getSyncedTicks('DAYS', data, dailyTickCount, interval_1);
                    }
                }
                if (weekly && weekly.enabled && weekly.interval) {
                    // A tick per week defaulting to 1 week
                    var interval_2 = weekly.interval;
                    var weeklyTickCount = this.getPeriodTickCount(tickCount, dataDateRangeInWeeks, interval_2);
                    if (weeklyTickCount) {
                        // Convert weeks interval into days
                        var weeklyInterval = interval_2 * 7;
                        return this.getSyncedTicks('DAYS', data, weeklyTickCount, weeklyInterval);
                    }
                }
                if (biweekly && biweekly.enabled && biweekly.interval) {
                    // A tick every two weeks defaulting to 2 weeks
                    var interval_3 = biweekly.interval;
                    var biweeklyTickCount = this.getPeriodTickCount(tickCount, dataDateRangeInWeeks, interval_3);
                    if (biweeklyTickCount) {
                        // Convert biweekly interval into days
                        var biweeklyInterval = interval_3 * 7;
                        return this.getSyncedTicks('DAYS', data, biweeklyTickCount, biweeklyInterval);
                    }
                }
                if (monthly && monthly.enabled && monthly.interval) {
                    // A tick per month defaulting to 1 month
                    var interval_4 = monthly.interval;
                    var monthlyTickCount = this.getPeriodTickCount(tickCount, dataDateRangeInMonths, interval_4);
                    if (monthlyTickCount) {
                        return this.getSyncedTicks('MONTHS', data, monthlyTickCount, interval_4);
                    }
                }
                if (quarterly && quarterly.enabled && quarterly.interval) {
                    // A tick per yearly quarter defaulting to 3 months
                    var interval_5 = quarterly.interval;
                    var quarterlyTickCount = this.getPeriodTickCount(tickCount, dataDateRangeInMonths, interval_5);
                    if (quarterlyTickCount) {
                        return this.getSyncedTicks('MONTHS', data, quarterlyTickCount, interval_5);
                    }
                }
                if (biannually && biannually.enabled && biannually.interval) {
                    // A tick twice per year defaulting to 6 months
                    var interval_6 = biannually.interval;
                    var biannuallyTickCount = this.getPeriodTickCount(tickCount, dataDateRangeInMonths, interval_6);
                    if (biannuallyTickCount) {
                        return this.getSyncedTicks('MONTHS', data, biannuallyTickCount, interval_6);
                    }
                }
                // If no matching tick count could be found we will need to check the years to a maximum of 10 years
                var interval = void 0;
                var yearlyTickCount = void 0;
                for (var i = 1; i <= 10; i++) {
                    var foundTickCount = this.getPeriodTickCount(tickCount, dataDateRangeInYears, i);
                    if (foundTickCount) {
                        interval = i;
                        yearlyTickCount = foundTickCount;
                        break;
                    }
                }
                if (interval && yearlyTickCount) {
                    return this.getSyncedTicks('YEARS', data, yearlyTickCount, interval);
                }
                console.warn("TickHandler: Could not find a suitable tick sync for tick count: " + tickCount);
                return [];
            }
        }
        console.warn("TickHandler: Could not get synced ticks from data, please check configuration");
        return [];
    };
    TickHandler.prototype.getPeriodTickCount = function (tickCount, dateRange, interval) {
        var resultingTickCount = Math.floor(dateRange / interval) + 1; // Account for starting tick
        if (resultingTickCount <= tickCount) { // Account for starting tick
            return resultingTickCount - 1;
        }
    };
    TickHandler.prototype.getSyncedTicks = function (period, data, iteration, interval) {
        // Save the first data point
        var syncedData = [data[0]];
        for (var i = 0; i < iteration; i++) {
            var dataPointDate = new Date(syncedData[i]);
            var targetDataPointDate = void 0;
            switch (period) {
                case 'YEARS':
                    targetDataPointDate = DatePickerUtils_1.addYears(dataPointDate, interval).getTime();
                    break;
                case 'MONTHS':
                    targetDataPointDate = DatePickerUtils_1.addMonths(dataPointDate, interval).getTime();
                    break;
                case 'DAYS':
                    targetDataPointDate = DatePickerUtils_1.addDays(dataPointDate, interval).getTime();
                    break;
            }
            var closestDataPoint = ChartBaseFunctions_1.getClosestValue(targetDataPointDate, data);
            syncedData.push(closestDataPoint);
        }
        return syncedData;
    };
    return TickHandler;
}());
exports.TickHandler = TickHandler;
